import {
  createSolverDiagnostic4x4,
  serializeSolverError,
  SOLVER_DIAGNOSTIC_STAGE_4X4,
} from './solver-diagnostics-4x4.js';

function now() {
  return globalThis.performance?.now?.() ?? Date.now();
}

function executionError(code, message, cause, details = {}) {
  const error = Object.assign(new Error(message, { cause }), { code });
  error.name = 'SolverExecutionError';
  Object.assign(error, details);
  return error;
}

const NON_FALLBACK_CODES = new Set([
  'INVALID_SOLVER_FACELETS',
  'INVALID_PATTERN',
  'RESULT_MISMATCH',
  'REDUCTION_REPLAY_MISMATCH',
  'CANCELED',
  'BUSY',
  'TIMEOUT',
]);

export class SolverExecutor {
  constructor({
    label,
    workerFactory,
    workerUrl,
    workerType = 'module',
    readyTimeoutMs = 5000,
    timeoutMs = 60000,
    fallback = null,
  }) {
    this.label = label;
    this.workerFactory = workerFactory;
    this.workerUrl = workerUrl;
    this.workerType = workerType;
    this.readyTimeoutMs = readyTimeoutMs;
    this.timeoutMs = timeoutMs;
    this.fallback = fallback;
    this.worker = null;
    this.readyPromise = null;
    this.readyResolve = null;
    this.readyReject = null;
    this.requestId = 0;
    this.pending = new Map();
    this.activeHooks = null;
  }

  #emit(stageOrEvent, details = {}) {
    if (!this.activeHooks?.onDiagnostic) return;
    const event = typeof stageOrEvent === 'string'
      ? createSolverDiagnostic4x4(stageOrEvent, {
        source: 'main',
        startedAtMs: this.activeHooks.startedAtMs,
        details,
      })
      : stageOrEvent;
    this.activeHooks.onDiagnostic(event);
  }

  #rejectAll(error) {
    this.readyReject?.(error);
    for (const request of this.pending.values()) request.reject(error);
    this.pending.clear();
  }

  #resetWorker() {
    this.worker?.terminate?.();
    this.worker = null;
    this.readyPromise = null;
    this.readyResolve = null;
    this.readyReject = null;
  }

  #handleFatalWorkerEvent(code, event, stage) {
    const details = serializeSolverError(event?.error ?? event, {
      code,
      stage,
      message: event?.message || (stage === 'messageerror' ? 'Worker消息无法反序列化' : 'Worker加载失败'),
      resourceUrl: event?.filename || this.workerUrl,
      filename: event?.filename ?? null,
      line: event?.lineno ?? null,
      column: event?.colno ?? null,
      dataType: stage === 'messageerror' ? typeof event?.data : undefined,
    });
    this.#emit(
      stage === 'messageerror' ? SOLVER_DIAGNOSTIC_STAGE_4X4.MESSAGE_ERROR : SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_ERROR,
      details,
    );
    const error = executionError(code, `${this.label} Worker ${details.message}`, event?.error, {
      workerStage: stage,
      workerStack: details.stack,
      resourceUrl: details.resourceUrl,
    });
    this.#rejectAll(error);
    this.#resetWorker();
  }

  #handleMessage(event) {
    const message = event.data ?? {};
    if (message.type === 'ready') {
      this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_READY, {
        workerUrl: message.workerUrl ?? this.workerUrl,
        workerType: message.workerType ?? this.workerType,
        blobUrl: String(message.workerUrl ?? this.workerUrl).startsWith('blob:'),
      });
      this.readyResolve?.(message);
      return;
    }
    if (message.type === 'diagnostic') {
      this.#emit(message.diagnostic);
      return;
    }
    const request = this.pending.get(message.id);
    if (!request) {
      if (message.id != null && this.pending.size > 0) {
        const pendingIds = [...this.pending.keys()];
        const error = executionError(
          'WORKER_PROTOCOL_MISMATCH',
          `${this.label} Worker返回了无法匹配的请求ID（收到 ${message.id}，等待 ${pendingIds.join(', ')}）`,
          null,
          { workerStage: 'message-protocol', resourceUrl: this.workerUrl },
        );
        this.#rejectAll(error);
        this.#resetWorker();
      }
      return;
    }
    if (message.type === 'status') request.onStatus(message.status);
    else if (message.type === 'result') {
      this.pending.delete(message.id);
      this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_RETURNED, {
        requestId: message.id,
        workerUrl: this.workerUrl,
      });
      request.resolve(message.result);
    } else if (message.type === 'error') {
      this.pending.delete(message.id);
      const remote = message.error ?? {};
      const cause = executionError(
        remote.code ?? 'WORKER_ERROR',
        remote.message ?? `${this.label} Worker发生未知异常`,
        null,
        {
          name: remote.name ?? 'WorkerError',
          workerStage: remote.stage ?? 'worker',
          workerStack: remote.stack ?? '',
          resourceUrl: remote.resourceUrl ?? this.workerUrl,
        },
      );
      if (remote.stack) cause.stack = remote.stack;
      request.reject(cause);
    } else {
      this.pending.delete(message.id);
      request.reject(executionError(
        'WORKER_PROTOCOL_MISMATCH',
        `${this.label} Worker返回了未知消息类型：${message.type ?? '空'}`,
      ));
    }
  }

  async #ensureWorker() {
    if (this.worker && this.readyPromise) return this.readyPromise;
    const expectedUrl = typeof this.workerUrl === 'string' ? this.workerUrl : this.workerUrl?.href;
    this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_CREATE_START, {
      workerUrl: expectedUrl,
      workerType: this.workerType,
      blobUrl: String(expectedUrl).startsWith('blob:'),
    });
    this.readyPromise = new Promise((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    try {
      this.worker = this.workerFactory();
    } catch (cause) {
      this.#resetWorker();
      throw executionError(
        'WORKER_CREATION_FAILED',
        `${this.label} Worker创建失败：${cause?.message ?? cause}`,
        cause,
        { resourceUrl: expectedUrl, workerStage: 'worker-create' },
      );
    }
    this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_CREATED, {
      workerUrl: expectedUrl,
      workerType: this.workerType,
      reused: false,
    });
    this.worker.addEventListener('message', (event) => this.#handleMessage(event));
    this.worker.addEventListener('error', (event) => this.#handleFatalWorkerEvent('WORKER_LOAD_FAILED', event, 'error'));
    this.worker.addEventListener('messageerror', (event) => this.#handleFatalWorkerEvent('WORKER_MESSAGE_ERROR', event, 'messageerror'));

    let timer;
    try {
      return await Promise.race([
        this.readyPromise,
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(executionError(
            'WORKER_READY_TIMEOUT',
            `${this.label} Worker在 ${this.readyTimeoutMs}ms 内没有发送ready`,
            null,
            { resourceUrl: expectedUrl, workerStage: 'worker-ready' },
          )), this.readyTimeoutMs);
        }),
      ]);
    } catch (cause) {
      this.#resetWorker();
      throw cause;
    } finally {
      clearTimeout(timer);
    }
  }

  async execute(payload, {
    onStatus = () => {},
    onDiagnostic = (_event) => {},
    onCompatibilityMode = (_error) => {},
    startedAtMs = Date.now(),
  } = {}) {
    this.activeHooks = { onDiagnostic, startedAtMs };
    const startedAt = now();
    try {
      await this.#ensureWorker();
      const id = ++this.requestId;
      const resultPromise = new Promise((resolve, reject) => {
        this.pending.set(id, { resolve, reject, onStatus });
        try {
          this.worker.postMessage({ id, ...payload, solveStartedAtMs: startedAtMs });
        } catch (cause) {
          this.pending.delete(id);
          reject(executionError(
            'WORKER_POST_FAILED',
            `${this.label} Worker消息发送失败：${cause?.message ?? cause}`,
            cause,
            { workerStage: 'post-message', resourceUrl: this.workerUrl },
          ));
        }
      });
      let timer;
      try {
        return await Promise.race([
          resultPromise,
          new Promise((_, reject) => {
            timer = setTimeout(() => reject(executionError(
              'TIMEOUT',
              `${this.label}求解超过 ${Math.round(this.timeoutMs / 1000)} 秒，已停止等待`,
              null,
              { workerStage: 'solve-timeout', resourceUrl: this.workerUrl },
            )), this.timeoutMs);
          }),
        ]);
      } finally {
        clearTimeout(timer);
      }
    } catch (workerError) {
      if (!this.fallback || NON_FALLBACK_CODES.has(workerError?.code)) throw workerError;
      this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.WORKER_ERROR, serializeSolverError(workerError, {
        resourceUrl: workerError?.resourceUrl ?? this.workerUrl,
      }));
      this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.FALLBACK_START, {
        reason: serializeSolverError(workerError),
        workerElapsedMs: Math.round(now() - startedAt),
      });
      this.#resetWorker();
      onCompatibilityMode(workerError);
      try {
        const result = await this.fallback(payload, { onStatus, onDiagnostic, startedAtMs, workerError });
        this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.FALLBACK_END, { success: true });
        return { ...result, compatibilityMode: true, workerError: serializeSolverError(workerError) };
      } catch (fallbackError) {
        this.#emit(SOLVER_DIAGNOSTIC_STAGE_4X4.FALLBACK_END, {
          success: false,
          error: serializeSolverError(fallbackError),
        });
        const combined = executionError(
          'COMPATIBILITY_FALLBACK_FAILED',
          `${this.label} Worker失败（${workerError.message}）；兼容回退也失败（${fallbackError?.message ?? fallbackError}）`,
          fallbackError,
          {
            workerStage: workerError.workerStage,
            workerStack: workerError.workerStack,
            resourceUrl: workerError.resourceUrl,
            workerError,
          },
        );
        throw combined;
      }
    } finally {
      this.activeHooks = null;
    }
  }

  cancel(reason = `${this.label}求解已取消`) {
    const error = executionError('CANCELED', reason);
    this.#rejectAll(error);
    this.#resetWorker();
  }

  dispose() {
    this.cancel(`${this.label}求解器已关闭`);
  }
}
